import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, Video, CalendarHeart, Globe2, ShieldCheck, Mail, Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { NextRitualBanner } from "@/components/Rituals";

const HERO = "https://static.prod-images.emergentagent.com/jobs/8c4c8cd5-6c40-4e6b-9b8a-de1282617ec2/images/a5a5e54920a9a2c888973facb09150b772b0c1119fa0cdc79cc24ca84ad88976.png";

const FEATURES = [
  { icon: Flame, title: "Global Matching", text: "Swipe through desirable members across the world and match with people who share your appetite." },
  { icon: Video, title: "Live Video Calls", text: "Take the tension face-to-face. Private, immersive video calls with your matches." },
  { icon: CalendarHeart, title: "Themed Nights", text: "Kinky Mondays, Masquerade Speed Dating & more curated events to keep every week electric." },
  { icon: ShieldCheck, title: "Discreet & Exclusive", text: "A members-only club built for adults who know exactly what they want." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [cfg, setCfg] = useState({ hero_headline: "", hero_subtext: "" });
  const [wlEmail, setWlEmail] = useState("");
  const [wlDone, setWlDone] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    const email = wlEmail.trim();
    if (!email || !email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setWlBusy(true);
    try {
      await api.post("/waitlist", { email });
      setWlDone(true);
      toast.success("You're on the list ✨");
    } catch (err) { toast.error("Couldn't join right now — please try again"); }
    finally { setWlBusy(false); }
  };

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/site-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen relative z-10" style={{ background: "#0A0A0A" }}>
      {/* Nav */}
      <header className="flex items-center justify-between px-6 md:px-12 py-6 relative z-20">
        <div>
          <div className="overline">Members Only · 18+</div>
          <div className="font-heading text-2xl md:text-3xl gold-text">Pleasure Empire</div>
        </div>
        <button data-testid="header-enter-btn" onClick={() => navigate(user ? "/discover" : "/auth")}
          className="btn-outline-gold px-5 py-2 rounded-full text-sm">
          {user ? "Enter Empire" : "Sign In"}
        </button>
      </header>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-75" style={{ filter: "brightness(1.25)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0A0A0A 12%, rgba(10,10,10,0.35) 55%, rgba(10,10,10,0.05))" }} />
        </div>
        {/* Magnetic floating gold orbs */}
        <div className="orb" style={{ width: 340, height: 340, top: "-60px", right: "8%", background: "rgba(212,175,55,0.45)", animation: "floatOrb 14s ease-in-out infinite" }} />
        <div className="orb" style={{ width: 240, height: 240, bottom: "-40px", left: "12%", background: "rgba(230,57,70,0.35)", animation: "floatOrb 18s ease-in-out infinite reverse" }} />
        <div className="orb" style={{ width: 180, height: 180, top: "40%", left: "45%", background: "rgba(184,134,11,0.3)", animation: "floatOrb 22s ease-in-out infinite" }} />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="relative z-10 max-w-2xl">
          <div className="overline mb-4">Where desire finds its match</div>
          <h1 className="font-heading text-5xl md:text-7xl leading-[0.95] mb-6">
            {cfg.hero_headline
              ? cfg.hero_headline
              : <>Enter the <span className="shimmer-gold italic">Pleasure Empire</span></>}
          </h1>
          <p className="text-lg text-[#A0A0A0] leading-relaxed mb-8 max-w-xl">
            {cfg.hero_subtext || "A worldwide, members-only playground for adults of every gender & desire. Match, meet, video-call and lose yourself in our nightly themed experiences."}
          </p>
          <div className="flex flex-wrap gap-4">
            <button data-testid="hero-join-btn" onClick={() => navigate("/auth")}
              className="btn-gold px-8 py-4 rounded-full text-base flex items-center gap-2">
              <Flame size={20} /> Join the Empire
            </button>
            <button data-testid="hero-explore-btn" onClick={() => navigate(user ? "/events" : "/auth")}
              className="btn-outline-gold px-8 py-4 rounded-full text-base flex items-center gap-2">
              <CalendarHeart size={20} /> See tonight's events
            </button>
          </div>
          <div className="flex items-center gap-4 mt-8 text-[#A0A0A0] text-sm flex-wrap">
            <span className="flex items-center gap-1"><Globe2 size={16} className="text-[#D4AF37]" /> Adults connecting in 90+ countries</span>
            {stats && (
              <>
                <span className="flex items-center gap-2" data-testid="stat-members">
                  <span className="gold-text font-semibold">{stats.total_members.toLocaleString()}</span> members
                </span>
                <span className="flex items-center gap-2" data-testid="stat-online">
                  <span className="w-2 h-2 rounded-full bg-green-400 live-dot" /> {stats.online_now.toLocaleString()} online now
                </span>
              </>
            )}
          </div>
        </motion.div>
      </section>

      {/* Electric promo banner */}
      <section className="px-6 md:px-12 py-6" data-testid="promo-banner">
        <div className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30"
          style={{ boxShadow: "0 0 60px rgba(212,175,55,0.18)" }}>
          <img src="https://static.prod-images.emergentagent.com/jobs/8c4c8cd5-6c40-4e6b-9b8a-de1282617ec2/images/91c6b7a15c0d0e1ef144effbbff7c87f07d0277cde24d7f5e9b8b79ecce005d1.jpeg"
            alt="Pleasure Empire" className="absolute inset-0 w-full h-full object-cover promo-kenburns" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,10,10,0.92) 5%, rgba(10,10,10,0.55) 45%, rgba(10,10,10,0.15))" }} />
          <div className="relative z-10 px-6 md:px-12 py-12 md:py-16 max-w-2xl">
            <div className="overline mb-3 promo-flicker">⚡ Spark something tonight</div>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight mb-4">
              Swipe. <span className="shimmer-gold italic">Spark.</span> Spoil.
            </h2>
            <p className="text-[#C7C7C7] text-base md:text-lg mb-6 max-w-lg">
              The most electric adults-only club online. Creators keep 100% of their earnings — fans, find your match and feel the heat. 🔥
            </p>
            <div className="flex flex-wrap gap-3">
              <button data-testid="promo-join-btn" onClick={() => navigate("/auth")}
                className="btn-gold px-7 py-3 rounded-full text-sm flex items-center gap-2">
                <Flame size={18} /> Join the Empire
              </button>
              <button data-testid="promo-creator-btn" onClick={() => navigate("/auth")}
                className="btn-outline-gold px-7 py-3 rounded-full text-sm">
                Become a Creator
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Early-access waitlist */}
      <section className="px-6 md:px-12 py-10" data-testid="waitlist-section">
        <div className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A1508] to-[#0A0A0A] px-6 md:px-12 py-10 md:py-14">
          <div className="orb" style={{ width: 220, height: 220, top: "-40px", right: "6%", background: "rgba(212,175,55,0.25)", animation: "floatOrb 16s ease-in-out infinite" }} />
          <div className="relative z-10 max-w-xl">
            <div className="overline mb-3">👑 Early access</div>
            <h2 className="font-heading text-3xl md:text-4xl leading-tight mb-3">
              Be first through the <span className="shimmer-gold italic">golden doors</span>
            </h2>
            <p className="text-[#A0A0A0] text-sm md:text-base mb-6">
              Join the waitlist for launch perks, founding-member pricing and first dibs on themed nights.
            </p>
            {wlDone ? (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="waitlist-success"
                className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] p-5">
                <div className="flex items-center gap-2 text-[#D4AF37] font-heading text-xl">
                  <Check size={20} /> You're on the list ✨
                </div>
                <p className="text-sm text-[#A0A0A0] mt-1 mb-4">We'll be in touch. Want to skip the line and set up your profile now?</p>
                <button data-testid="waitlist-create-account" onClick={() => navigate("/auth")}
                  className="btn-gold px-6 py-3 rounded-full text-sm inline-flex items-center gap-2">
                  Create your account <ArrowRight size={16} />
                </button>
              </motion.div>
            ) : (
              <form onSubmit={joinWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-md">
                <div className="relative flex-1">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
                  <input data-testid="waitlist-email" type="email" required placeholder="your@email.com"
                    value={wlEmail} onChange={(e) => setWlEmail(e.target.value)}
                    className="pe-input w-full rounded-full pl-11 pr-4 py-3" />
                </div>
                <button data-testid="waitlist-submit" type="submit" disabled={wlBusy}
                  className="btn-gold px-7 py-3 rounded-full text-sm whitespace-nowrap disabled:opacity-60">
                  {wlBusy ? "Joining…" : "Join the waitlist"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 py-20 max-w-6xl mx-auto">
        <div className="overline mb-2">Why members stay</div>
        <h2 className="font-heading text-3xl md:text-5xl mb-12">Everything an adult could crave</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="pe-card glow-hover rounded-2xl p-6" data-testid={`feature-${i}`}>
              <f.icon className="text-[#D4AF37] mb-4" size={28} />
              <h3 className="font-heading text-2xl mb-2">{f.title}</h3>
              <p className="text-sm text-[#A0A0A0] leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Next weekly ritual countdown */}
      <NextRitualBanner />

      {/* CTA */}
      <section className="px-6 md:px-12 py-20 text-center border-t border-[#2A2A2A]">
        <h2 className="font-heading text-4xl md:text-6xl mb-4">Your seat at the table awaits</h2>
        <p className="text-[#A0A0A0] mb-8">Create your profile in seconds. Choose your vibe. Let the games begin.</p>
        <button data-testid="cta-join-btn" onClick={() => navigate("/auth")}
          className="btn-gold px-10 py-4 rounded-full text-base">Claim your membership</button>
        <div className="mt-5">
          <button data-testid="cta-charter-btn" onClick={() => navigate("/charter")}
            className="text-sm text-[#D4AF37] hover:text-white transition-colors underline underline-offset-4">
            Read the Empire Charter →
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-12 text-xs text-[#A0A0A0]">
          <button data-testid="footer-charter" onClick={() => navigate("/charter")} className="hover:text-[#D4AF37] transition-colors">Empire Charter</button>
          <button data-testid="footer-terms" onClick={() => navigate("/legal?s=terms")} className="hover:text-[#D4AF37] transition-colors">Terms of Service</button>
          <button data-testid="footer-privacy" onClick={() => navigate("/legal?s=privacy")} className="hover:text-[#D4AF37] transition-colors">Privacy Policy</button>
          <button data-testid="footer-content" onClick={() => navigate("/legal?s=content")} className="hover:text-[#D4AF37] transition-colors">Content & Consent</button>
          <button data-testid="footer-community" onClick={() => navigate("/legal?s=community")} className="hover:text-[#D4AF37] transition-colors">Community Guidelines</button>
          <button data-testid="footer-takedown" onClick={() => navigate("/legal?s=takedown")} className="hover:text-[#D4AF37] transition-colors">Report / Takedown</button>
          <button data-testid="footer-2257" onClick={() => navigate("/legal?s=2257")} className="hover:text-[#D4AF37] transition-colors">18 U.S.C. 2257</button>
          <a data-testid="footer-support" href={`mailto:${cfg.support_email || "support@pleasureempire.co"}`} className="hover:text-[#D4AF37] transition-colors">Support &amp; Contact</a>
        </div>
        <p className="text-xs text-[#A0A0A0] mt-6">Pleasure Empire · Strictly 18+ · Play safe, play consensual.</p>
      </section>
    </div>
  );
}
